import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import {
  BusinessRuleItemType,
  BusinessRuleSolutionEnum,
  NUMBER_STRING_HASH,
  STRING_ANY_HASH,
  STRING_BOOLEAN_HASH,
  STRING_STRING_HASH,
} from '../constants/globals';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { emitInvoiceStatusDataItem } from '../functions/invoiceStatusDataItemSink';
import { AppointmentInsuranceType } from './Appointment.type';
import { KnownClearingHouses } from './BillingCode.type';
import type { ClaimLineEventType, SetAsFinalSkippedLine } from './ClaimLineEvent.type';
import type { ClaimQuickPickSummary } from './ClinicBillingSettings.type';
import { ClaimResponseEraStatus, type ClaimResponseEraHistoryEntry } from './Era.type';
import { IntegrationTypeEnum } from './Integration.type';
import type { Patient, PatientBalanceInfo } from './Patient.type';
import type { ClaimInfoResolutionSource } from './PatientClaimInfo.type';
import { PatientPrimaryInsured, PrimaryInsuredTypes } from './PatientInsurance.type';
import { PatientInvoice } from './PatientInvoice.type';
import {
  isaRefundItem,
  PatientTransaction,
  TransactionItemSubtypeEnum,
  TransactionPatientSummaryType,
} from './PatientTransaction.type';
import { ClaimInfo } from './PatientVisit.type';
import { ArchivedPayorLineItemSnapshot, PayorLineItemType } from './Payor.type';
import type { UserSettingsBillingType } from './UserSettingsBilling.type';

dayjs.extend(dayOfYear);

export type AdjustmentActionType = {
  action: string;
  type: string;
  row: any;
  pk: string;
  parentPk?: string;
  manual?: boolean;
};

export type AdjustInvoiceType = {
  clinicId: number;
  id: number;
  actions: AdjustmentActionType[];
  sidecar?: { [key: string]: boolean };
};

export enum KnownSidecarEnum {
  Invoices = 'invoices',
  Transaction = 'transaction',
}

export type KnownSidecars = {
  [KnownSidecarEnum.Invoices]?: IntegrationInvoice[];
  [KnownSidecarEnum.Transaction]?: PatientTransaction;
};

export type BusinessRuleIssueResponse = {
  count: number;
  issues: BusinessRuleItemType[]; // These are things that _must_ be addressed.
  notes: BusinessRuleItemType[]; // These are warnings or suggestions, etc. Not errors.
  data?: any;
  name?: keyof typeof BusinessRuleSolutionEnum;
  invoice?: IntegrationInvoice;
};

/**
 * This allows fine granularity of whether an option appears in
 * the adjustment tool.
 */
export const SupportedInvoiceActions = {
  [['era-to-cre', 'era'].join('.')]: true,
  [['status-to-post', 'cre'].join('.')]: true,
  [['status-to-applied', 'cre'].join('.')]: true,
  [['apply', 'pli'].join('.')]: true,
  [['post', 'cre'].join('.')]: true,
  [['apply-adj', 'cre'].join('.')]: true,
  [['unlock', 'cre'].join('.')]: true,
  [['assign-policy', 'invoice'].join('.')]: true,
  [['recreate', 'pli'].join('.')]: true,
  [['remove-item', 'transaction'].join('.')]: true,
  [['delete', 'payment'].join('.')]: true,
  // [13277519972] Marks the claim response the merges after it go into; the server does nothing.
  [['merge-target', 'cre'].join('.')]: true,
  [['merge', 'cre'].join('.')]: true,
  [['unmerge', 'cre'].join('.')]: true,
} as STRING_BOOLEAN_HASH;

/**
 * [13277519972] `row` of a `merge.cre` adjustment action; `pk` is the absorbed claim
 * response's id.
 */
export type MergeClaimResponseActionRow = {
  survivorId: number;
};

enum ClaimDelayReasonEnum {
  EligibilityUnknown = 1,
  Litigation = 2,
  AuthorizationDelays = 3,
  CertifyingProvider = 4,
  SupplyingBillingForms = 5,
  CustomApplianceDelivery = 6,
  ThirdPartyProcessing = 7,
  EligibilityDetermination = 8,
  OriginalClaimRejectedOrDenied = 9,
  AdministrationPriorApprovalProcess = 10,
  Other = 11,
  NaturalDisaster = 15,
}

const ClaimDelayReasonText: { [key: number]: string } = {
  1: 'Proof of Eligibility Unknown',
  2: 'Litigation',
  3: 'Authorization Delays',
  4: 'Delay in Certifying Provider',
  5: 'Delay in Supplying Billing Forms',
  6: 'Delay in Delivery of Custom Appliances',
  7: 'Third Party Processing Delay',
  8: 'Delay in Eligibility Determination',
  9: 'Original Claim Rejected or Denied Due to Reason Unrelated to the Billing Limitation Rules',
  10: 'Administration Delay in the Prior Approval Process',
  11: 'Other',
  15: 'Natural Disaster',
};

export const ClaimDelayReasonOptions = Object.keys(ClaimDelayReasonEnum)
  .filter(key => /^\d+$/.test(key))
  .map(key => ({
    text: ClaimDelayReasonText[Number(key)] || `-- woops ${key} --`,
    value: key,
  }))
  .sort((a, b) => (a.text > b.text ? 1 : -1));

enum InvoiceGenerationEnum {
  Original = 'Original',
  Replacement = 'Replacement',
  Void = 'Void',
}

/**
 * Data structure to validate inputs.
 */
export const InvoiceGenerationEdiXref: { [key: string]: string } = {
  [InvoiceGenerationEnum.Original]: '1',
  [InvoiceGenerationEnum.Replacement]: '7',
  [InvoiceGenerationEnum.Void]: '8',
  '1': InvoiceGenerationEnum.Original,
  '7': InvoiceGenerationEnum.Replacement,
  '8': InvoiceGenerationEnum.Void,
};

/**
 * At this point, this is _only_ to select what we'll send to the EDI
 * for the generation. So, the values are the EDI field values and not
 * the strings from the enum.
 */
export const InvoiceGenerationOptions = Object.keys(InvoiceGenerationEnum).map(key => ({
  value: InvoiceGenerationEdiXref[key],
  text: key,
}));

export type InvoiceStatusSummaryResponse = {
  status: InvoiceStatusEnum;
  count: number;
  balance?: number;
  hasClaimLineData?: boolean;
};

export type IntegrationInvoiceListResponse = {
  count: number;
  data: IntegrationInvoice[];
  page: number;
  summary?: InvoiceStatusSummaryResponse[];
  quickPicks?: ClaimQuickPickSummary[];
  nextAtRiskAt?: number | null;
  serverNow?: number | null;
};

export enum InvoiceStatusEnum {
  // Hold - Claim is on hold and will not be processed until the hold is
  // removed. Stops the claim from being processed without manual intervention.
  Hold = 'Hold',
  // Waiting on Other - Claim can not be generated until the claim ahead
  // of it in the priority order has been denied or paid. This can not be
  // set by a user. The system auto-sets this for downstream payors.
  Waiting = 'Waiting on Other',
  // Pending - Information that is required for claim generation is missing.
  Pending = 'Pending',
  // Unsubmitted - Claim is ready to be downloaded or submitted (this an
  // auto-status, users can not change a claim status to this).
  Unsubmitted = 'Unsubmitted',
  // Queued - Claim has been queued for batch submission and will be
  // processed shortly. Prevents duplicate submissions while in queue.
  Queued = 'Queued',
  // Submitted -Claim has been submitted to the Payor for reimbursement.
  Submitted = 'Submitted',
  // Received - this means that the Payor has acknowledged that they have
  // received the claim.
  Received = 'Received',
  // Rejected - Claim has not been processed by the Payor due to errors or
  // invalid information discovered during the initial validation process.
  Rejected = 'Rejected',
  // Denied - Claim has undergone review and the Payor determined the claim
  // to be ineligible for reimbursement.
  Denied = 'Denied',
  // Partially Paid - Claim has been partially paid and the clinic is
  // awaiting another claim response.
  Partial = 'Partially Paid',
  // Paid - Claim has been processed and payment from Payor has been received.
  Paid = 'Paid',
  // This is a status just for patient invoices that denotes submitted but not paid
  Unpaid = 'Unpaid',
  // Schizophrenic status.
  Reprocessed = 'Reprocessed', // Allow for paid and denied to transition to this.
}

export const FrontEndSummaryStatuses = [
  InvoiceStatusEnum.Pending,
  InvoiceStatusEnum.Unsubmitted,
  InvoiceStatusEnum.Submitted,
  InvoiceStatusEnum.Rejected,
  InvoiceStatusEnum.Denied,
];

/**
 * Record-level Claim Info (SPEC §0/AC-7): statuses in which an invoice still FOLLOWS the
 * purchase's shared claim-info copy (and in which a locked purchase remains
 * editable). Invoices in any other status display their own per-transmission
 * frozen value. Statuses gate edit PERMISSION only — locking itself is always
 * a generation event, never a status.
 */
export const CLAIM_INFO_EDITABLE_STATUSES = [
  InvoiceStatusEnum.Hold,
  InvoiceStatusEnum.Waiting,
  InvoiceStatusEnum.Pending,
  InvoiceStatusEnum.Unsubmitted,
  InvoiceStatusEnum.Rejected,
  InvoiceStatusEnum.Denied,
] as const;

export const isClaimInfoEditableStatus = (status?: InvoiceStatusEnum | string | null): boolean =>
  !!status && (CLAIM_INFO_EDITABLE_STATUSES as readonly string[]).includes(status);

export const isAdjudicatedStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return (
    i.status === InvoiceStatusEnum.Denied ||
    i.status === InvoiceStatusEnum.Unpaid ||
    i.status === InvoiceStatusEnum.Paid
  );
};

/**
 * [2026-01-16 for 10040640129 by Brian] Removed status-based gating. When
 * the user explicitly requests 837 generation, honor that request. Invoice
 * status is advisory - users and BAs can already modify it via various paths.
 * The signature-change flow archives PLIs but keeps the invoice status
 * unchanged; blocking regeneration based on status created unnecessary
 * friction. The block was: Pending, Unsubmitted, Rejected only.
 */
export const isaValidBatchSetStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return true;
};

export const isaInvoiceAdjustableStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return (
    i.status === InvoiceStatusEnum.Denied ||
    i.status === InvoiceStatusEnum.Unpaid ||
    i.status === InvoiceStatusEnum.Paid ||
    i.status === InvoiceStatusEnum.Received ||
    i.status === InvoiceStatusEnum.Reprocessed ||
    i.status === InvoiceStatusEnum.Rejected
  );
};

// [2025-07-28.1207 by Brian via Emily] Changes to a claim (invoice) status
// should ONLY automatically un-finalize payor line items IF the claim status
// of the finalized (last) ERA is moved to Reprocessed OR Rejected.
export const isaClearFinalizedStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return ClearFinalizedStatusHash[i.status as keyof typeof ClearFinalizedStatusHash];
};

export const ClearFinalizedStatusHash = {
  [InvoiceStatusEnum.Reprocessed]: true,
  [InvoiceStatusEnum.Rejected]: true,
};

export const SupportedFinalizedStatuses = {
  [InvoiceStatusEnum.Paid]: true,
  [InvoiceStatusEnum.Unpaid]: true,
  [InvoiceStatusEnum.Denied]: true,
};

export const isaBrokenInvoice = (invoice: IntegrationInvoice) => {
  return (invoice?.priority ?? 0) <= 0 && canReverseSubmit(invoice);
};

export const canReverseSubmit = (invoice: IntegrationInvoice) => {
  return (
    invoice?.status === InvoiceStatusEnum.Submitted ||
    invoice?.status === InvoiceStatusEnum.Reprocessed ||
    invoice?.status === InvoiceStatusEnum.Received
  );
};

/**
 * Fix 7 (BA): Users must be able to generate/regenerate a claim when the invoice is
 * Unsubmitted/Rejected (and downstream-ready Pending), regardless of whether prior adjudication
 * exists elsewhere on the purchase (applied/posted).
 *
 * NOTE: This is strictly a "can we submit now?" gate. It intentionally does NOT attempt to
 * encode adjudication rules; those are handled elsewhere (e.g., edit protections).
 */
export const isSubmittableClaim = (invoice: IntegrationInvoice) => {
  return (
    invoice?.status === InvoiceStatusEnum.Unsubmitted ||
    invoice?.status === InvoiceStatusEnum.Rejected ||
    invoice?.status === InvoiceStatusEnum.Pending ||
    invoice?.status === InvoiceStatusEnum.Queued
  );
};

export const InvoiceSubmittedStatusList = [
  InvoiceStatusEnum.Submitted,
  InvoiceStatusEnum.Received,
  InvoiceStatusEnum.Partial,
];

export const isStatusWaitingSupported = (status: InvoiceStatusEnum) => {
  switch (status) {
    case InvoiceStatusEnum.Pending:
    case InvoiceStatusEnum.Unsubmitted:
      return true;
    default:
      return false;
  }
};

export const InvoiceStatusTips: STRING_STRING_HASH = {
  [InvoiceStatusEnum.Hold]: 'All claim processing is disabled until the hold is removed.',
  [InvoiceStatusEnum.Waiting]: [
    'Claim can not be generated until the claim ahead',
    'of it in the priority order has been denied or paid.',
  ].join(' '),
  [InvoiceStatusEnum.Pending]: 'Information that is required for claim generation is missing.',
  [InvoiceStatusEnum.Queued]: 'Claim is queued for batch submission.',
  [InvoiceStatusEnum.Unsubmitted]: 'Claim is ready to be submitted.',
  [InvoiceStatusEnum.Submitted]: 'Claim has been submitted to the Payor for reimbursement.',
  [InvoiceStatusEnum.Received]: 'Indicates that the payor has acknowledged receipt of the claim.',
  [InvoiceStatusEnum.Rejected]: [
    'Payor has not processed the claim due to errors or',
    'invalid information discovered during the validation process.',
  ].join(' '),
  // Denied - Claim has undergone review and the Payor determined the claim
  // to be ineligible for reimbursement.
  [InvoiceStatusEnum.Denied]: [
    'Payor has reviewed the claim and determined it',
    'to be ineligible for reimbursement',
  ].join(' '),
  [InvoiceStatusEnum.Partial]: [
    'Claim has been partially paid and the clinic is',
    'awaiting another claim response.',
  ].join(' '),
  [InvoiceStatusEnum.Paid]: 'Claim has been processed and payment has been received.',
  [InvoiceStatusEnum.Reprocessed]: 'Claim has been reprocessed.',
};

/**
 * The trick here is that the indices of the values and the
 * keys are in the same order so the options can be created
 * correctly with the ones a user can't select filtered out.
 */
let vals: any[] = Object.values(InvoiceStatusEnum).filter(
  v => v !== 'Unsubmitted' && v !== 'Waiting on Other' && v !== 'Queued'
);

const AllInvoiceStatusDisplay: STRING_STRING_HASH = {};
vals = Object.values(InvoiceStatusEnum);
const overRide: STRING_STRING_HASH = {
  [InvoiceStatusEnum.Waiting]: 'Waiting',
  [InvoiceStatusEnum.Partial]: 'Partial',
};
export const AllInvoiceStatusOptions = Object.keys(InvoiceStatusEnum).map((key, idx) => {
  AllInvoiceStatusDisplay[vals[idx]] = key;

  return { text: overRide[vals[idx]] ?? vals[idx], value: key };
});

export const AllowedInvoiceStatusTransitions: STRING_ANY_HASH = {
  [InvoiceStatusEnum.Submitted]: {
    [InvoiceStatusEnum.Received]: true,
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Unsubmitted]: true,
  },
  [InvoiceStatusEnum.Received]: {
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Paid]: true,
  },
  [InvoiceStatusEnum.Rejected]: {
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Submitted]: true,
  },
  [InvoiceStatusEnum.Denied]: {
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Reprocessed]: true,
  },
  [InvoiceStatusEnum.Partial]: {},
  [InvoiceStatusEnum.Paid]: {
    [InvoiceStatusEnum.Reprocessed]: true,
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
  },
  [InvoiceStatusEnum.Unpaid]: {
    [InvoiceStatusEnum.Reprocessed]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
  [InvoiceStatusEnum.Pending]: {
    [InvoiceStatusEnum.Hold]: true,
  },
  [InvoiceStatusEnum.Unsubmitted]: {
    [InvoiceStatusEnum.Hold]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
  [InvoiceStatusEnum.Queued]: {
    [InvoiceStatusEnum.Submitted]: true,
    [InvoiceStatusEnum.Pending]: true,
    [InvoiceStatusEnum.Unsubmitted]: true,
  },
  [InvoiceStatusEnum.Waiting]: {
    [InvoiceStatusEnum.Pending]: true,
    [InvoiceStatusEnum.Hold]: true,
  },
  [InvoiceStatusEnum.Hold]: {
    [InvoiceStatusEnum.Pending]: true,
  },
  [InvoiceStatusEnum.Reprocessed]: {
    [InvoiceStatusEnum.Paid]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
};

/**
 * 277 processing policy
 *
 * Rationale:
 * - Submitted/Received/Reprocessed: pre-adjudicative states; a 277 rejection should move the claim to rejected.
 * - Pending/Unsubmitted/Waiting: still allow rejection and notify via activity (unexpected but valid).
 * - Hold: administrative hold; do not move off hold automatically. Attach issues and record activity.
 * - Paid/Partial/Unpaid/Denied: adjudicated outcomes; do not unwind automatically. Attach issues and record activity.
 */
export type Invoice277PolicyEntry = {
  allow: boolean;
  changeStatus: InvoiceStatusEnum | null;
  /** Status an accepted (issue-free) 277 moves the invoice to; absent leaves it where it is. */
  acceptStatus?: InvoiceStatusEnum;
  warn: boolean; // When true, also record a transaction activity explaining the exception semantics
};

export const Invoice277ProcessingPolicy: Record<InvoiceStatusEnum, Invoice277PolicyEntry> = {
  [InvoiceStatusEnum.Submitted]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    acceptStatus: InvoiceStatusEnum.Received,
    warn: true,
  },
  [InvoiceStatusEnum.Received]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Reprocessed]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Rejected]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },

  [InvoiceStatusEnum.Pending]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Unsubmitted]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Queued]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Waiting]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },

  // Administrative hold should not be auto-cleared
  [InvoiceStatusEnum.Hold]: { allow: true, changeStatus: null, warn: true },

  // Adjudicated outcomes should not be unwound automatically
  [InvoiceStatusEnum.Paid]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Partial]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Unpaid]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Denied]: { allow: true, changeStatus: null, warn: true },
};

export type InvoiceInsert = {
  balance?: number;
  billedAmount?: number;
  billingKey: string;
  clinicId: number;
  createdAt?: number;
  estimated?: number;
  estimatedBalance?: number;
  insuranceId?: string;
  invoiceData?: any;
  issuedDate?: number;
  patientId: string;
  patientResponsibility?: number;
  payorId?: number;
  status: string;
  writeOffTotal?: number;
  finalized?: boolean;
  rev?: number;
  // Born held (13049432419): the clinic requires a billable code and the
  // purchase has none yet. Omitted = live.
  deleted?: boolean;
};

export type InvoiceEdiMetaType = {
  delayReason?: string;
  generation?: string;
  claimControlNumber?: string;
};
export interface FinalizeItem {
  id: number;
  code: string;
  finalized: boolean;
  claimResponseEraId: number;
  claimStatus: ClaimResponseEraStatus;
  eraId: number;
  clinicId: number;
  lineItemControlNumber?: string;
  billingKey?: string;
  remitDate?: number | null;
}

/**
 * Response body shared by the per-line toggle branches of
 * `integrationInvoiceStatusUpdate` (`finalize-purchase-items`,
 * `dispute-purchase-items`, `undispute-purchase-items`).
 */
export type ServiceLineToggleResponse = {
  message: string;
  lineItemIds: number[];
  updatedItems: FinalizeItem[];
  claimLineEvents?: ClaimLineEventType[];
  payorLineItems?: PayorLineItemType[];
  patientInvoice?: IntegrationInvoice;
  patientBalance?: PatientBalanceInfo;
  skipped?: SetAsFinalSkippedLine[];
};

export type Invoice = InvoiceInsert & {
  // isBillingStarted is NOT ALWAYS FILLED IN! Make sure the query
  // you're using is pulling it if you need it!!!
  //The claim control number is provided in the claim response from the payor...
  asciiPayorId?: string;
  billingCodes?: string[];
  claimControlNumber?: string;
  controlNumber?: number;
  currentControlNumber?: string;
  delayReason?: string;
  deleted?: boolean;
  dispatched?: boolean; //printed or emailed invoice
  eraIds?: string[];
  exchangeId?: string;
  generation?: string;
  id: number;
  interchangeControlNumber?: number;
  invoiceData?: any | PatientInvoice;
  isBillingStarted?: boolean; // Calculated from PayorLineItem.
  issuedDate?: number;
  legalName?: string;
  number: string;
  priority?: number;
  priorityLast?: number;
  statusAt?: number;
  statusData?: InvoiceStatusData | null;
  superBill?: boolean;
  tz?: string; // Timezone: Pulled from ClinicLocation, not in the Invoice table.
  uiLegalName?: string;
  // Optional count of insurance-billable service items on the purchase
  insuranceBillableItemCount?: number;
  // UI-specific metadata in some circumstances.
  _meta?: {
    userSettingsBilling?: UserSettingsBillingType;
    patientRecord?: Patient;
  };
};

export type SuperbillDisplay = {
  total: number;
  totalPaid?: number;
  onlyBillable: boolean;
};

export type ExtendedGetInvoiceResponse = GetInvoiceResponse & {
  _superbill?: SuperbillDisplay;
};

/**
 * Response of the super-bills list endpoint (superBillsList). Stale patient
 * invoices are rev-upgraded within a time budget per request (12915850138);
 * `staleRemaining` counts the ones still below INVOICE_REV_CURRENT so the
 * frontend can prompt the user to run the request again.
 */
export type SuperBillsListResponse<TInvoice = Invoice> = {
  superBills: TInvoice[];
  staleRemaining: number;
};

export enum GetIntegrationInvoiceUsage {
  EraSmartPosting = 'era-smart-posting',
  History = 'history',
}

/**
 * Integration invoices have a lot of extra details than normal invoices.
 * To retrieve them, joins to multiple tables are necessary so use the
 * normal invoice most of the time. Use this type of invoice when specific
 * details about whether an EDI document needs to be created. The extra
 * fields are needed for running the business rules: UI and back end.
 *
 * Most of the time EDI creation is not an issue when dealing with invoices
 * so the extra table joins are not needed.
 */
export type IntegrationInvoice = Invoice & {
  /** Current insurance-billable purchase codes; empty when no items are billable. */
  billableBillingCodes?: string[];
  billingProfile?: IntegrationInvoiceBillingProfile;
  billingProfileId?: number;
  billingProfileName?: string;
  billingProfileType?: string;
  // Optional count of insurance-billable service items (for UI gating like superbill button)
  insuranceBillableItemCount?: number;
  claimInfo?: ClaimInfo;
  /** True when a claim-info snapshot exists for this encounter (the claim is
   * locked). Gates the "Make correction" button so it only shows post-generation. */
  claimInfoLocked?: boolean;
  /** Which level the hydrated claimInfo resolved from. */
  claimInfoSource?: ClaimInfoResolutionSource;
  electronicBilling: boolean;
  hasClaimResponse?: boolean;
  has837Context?: boolean;
  /** Electronic claim with no usable snapshot whose 1500 the server rebuilds from current
   * purchase data (file-world channels only; connector claims refuse). */
  cms1500Regenerable?: boolean;
  integrationActive?: boolean;
  integrationId?: number;
  integrationType?: IntegrationTypeEnum;
  interchangeControlNumber?: number;
  isAtRisk?: boolean;
  isFlagged?: boolean;
  legalName: string;
  noteCount?: number;
  // Note bodies, newest first. Only populated on includeNoteText fetches
  // (the claims CSV export); the grid works from noteCount.
  notes?: GetInvoiceNotesRow[];
  patient: TransactionPatientSummaryType;
  payorLineItems?: PayorLineItemType[];
  payorLineItemsOther?: PayorLineItemType[];
  claimLineEvents?: ClaimLineEventType[];
  claimLineEventsFull?: ClaimLineEventType[]; // only on `use=history` fetches
  claimResponses?: ClaimResponseEraHistoryEntry[]; // only on `use=history` fetches
  payorFax?: string;
  pos?: string;
  primaryInsured?: PatientPrimaryInsured & {
    memberId: string;
    type: PrimaryInsuredTypes;
  };
  provider?: IntegrationInvoiceProvider;
  providerDisplayName: string;
  touched?: number;
  transactionDate?: number;
  transactionItems?: uiPayorInvoiceLineItem[];
  clearinghouse?: KnownClearingHouses;
  insurances?: AppointmentInsuranceType[];
  /** Policy (PatientInsurance) active flag; when false, policy was marked inactive. */
  policyActive?: boolean;
  /** When policy was marked inactive (set when active goes 0). Used for alert copy. */
  policyInactivatedAt?: number | null;
  /** Patient policy type (primary/secondary/tertiary/other) for billing order mismatch alert. */
  policyType?: string;
  /** Tracks the status before an invoice was moved to Queued, so it can be reverted on failure. */
  _previousStatus?: InvoiceStatusEnum;
  /** Billing order for this insurance on the appointment (from AppointmentInsurance). Null = patient invoice. */
  billingPriority?: number | null;
};

/**
 * Invoice shape returned by the `transactionList` endpoint.
 * Core fields from Invoice + computed SQL columns.
 */
export type TransactionListInvoice = Pick<
  Invoice,
  'billedAmount' | 'balance' | 'status' | 'id' | 'billingKey'
> & {
  displayName: string;
  paid: number;
};

export type IntegrationInvoiceBillingProfile = {
  id: number;
  name: string;
  type: string;
  npi: string;
  taxId: string;
};

export type IntegrationInvoiceProvider = {
  name: string;
  npi: string;
  taxonomyCode: string;
};

export type IntegrationInvoiceProcessedType = {
  [key: number]: {
    issues: BusinessRuleItemType[];
    notes: BusinessRuleItemType[];
    success: boolean;
    /**
     * The billed $ createBatchSet wrote to the invoice on the submit path
     * (post partial-claim selection — exactly what went on the claim).
     * Consumers (batch837Drain analytics) read it from here instead of
     * re-querying what CBS just computed (INFRA-6, Brian 2026-07-21).
     */
    billedAmount?: number;
  };
};

export type InvoiceSavedState = Pick<IntegrationInvoice, 'id' | 'status' | 'statusData'>;

/**
 * Return shape of createBatchSet(). Used by batch837Drain
 * to avoid `any` on the result. Fields beyond meta/processed are optional
 * because early-return paths omit them.
 */
export type CreateBatchSetResult = {
  meta?: {
    count?: number;
    issues?: BusinessRuleItemType[];
  };
  processed?: IntegrationInvoiceProcessedType;
  invoice?: IntegrationInvoice | null;
  invoiceStates?: InvoiceSavedState[];
  exchangeId?: string;
  [key: string]: unknown;
};

/**
 * The createBatchSet working structure captured for a connector claim's
 * submission-time CMS-1500. Persistence keeps `processed` (with the invoice
 * fields the renderer is known not to read removed) plus the `source.biller`
 * slice — fillForm renders boxes 25/32/33 from it — and drops the rest of
 * `source`, which is build-time input the renderer never reads.
 */
export type ClaimBuildContext = {
  source: Record<string, unknown>;
  processed: {
    invoices: IntegrationInvoice[];
  };
  [key: string]: unknown;
};

export const CONNECTOR_CLAIM_CONTEXT_VERSION = 'compact-structure-v1' as const;

/** The decoded, submission-time connector snapshot returned by the envelope. */
export type ConnectorClaimContext = Omit<ClaimBuildContext, 'source'> & {
  contextVersion: typeof CONNECTOR_CLAIM_CONTEXT_VERSION;
  /** Absent when the build structure carried no biller. */
  source?: { biller?: { provider?: Record<string, unknown>; settings?: Record<string, unknown> } };
};

/** Minimum structure consumed by the CMS-1500 renderer across stored and live paths. */
export type CMS1500ClaimData = {
  processed: {
    invoices: (IntegrationInvoice & { completeListofDiagnoses?: unknown[] })[];
  };
};

export type IntegrationInvoicePayorSummary = {
  payorId: number;
  billedAmount: number;
  balance: number;
};

export type IntegrationInvoiceStatusUpdateResponse = {
  invoices: IntegrationInvoice[];
  issues: BusinessRuleItemType[];
  notes: BusinessRuleItemType[];
};

/**
 * A trivial little helper to generate a baseId for an invoice. The
 * 'id' and 'baseId' are added together to get the invoiceId that is
 * sent to the payor in EDI.
 *
 * This _may_ be unnecessary, but it seemed weird to have invoice nos
 * of 1, 2, 3,... So, we go off the base of the current year minus 2020
 * followed by the day of the year and then the id of the invoice. This
 * should always be unique but harder to guess across clinics.
 *
 * @returns
 */
export const invoiceBaseId = () => {
  const yearPart = Number(dayjs().format('YYYY')) - 2020,
    dayPart = `000${dayjs().dayOfYear()}`.slice(-3);

  return Number(`${yearPart}${dayPart}`);
};

/**
 * Yes, I know, this goes to ELEVEN!!!  Wonder if the EDI
 * boffins did that on purpose!
 */
enum PayorPriorityEnum {
  Primary = 'P',
  Secondary = 'S',
  Tertiary = 'T',
  Fourth = 'A',
  Fifth = 'B',
  Sixth = 'C',
  Seventh = 'D',
  Eighth = 'E',
  Nineth = 'F',
  Tenth = 'G',
  Eleventh = 'H',
}

export const PayorPriorityDisplay: NUMBER_STRING_HASH = {
  [-1]: '-na-',
  0: '-na-',
  1: 'Primary',
  2: 'Secondary',
  3: 'Tertiary',
  4: 'Fourth',
  5: 'Fifth',
  6: 'Sixth',
  7: 'Seventh',
  8: 'Eighth',
  9: 'Nineth',
  10: 'Tenth',
  11: 'Eleventh',
};

vals = Object.values(PayorPriorityEnum);
const PayorPriorityOptions = Object.keys(PayorPriorityEnum).map((key, idx) => ({
  value: vals[idx],
  text: key,
}));

export const PayorPriorityIdText: NUMBER_STRING_HASH = PayorPriorityOptions.reduce((a, c, i) => {
  a[i + 1] = c.text;

  return a;
}, {} as NUMBER_STRING_HASH);

type respType = {
  serviceTotal: number;
  involvesSpinalManipulation: boolean;
  patientAmountPaid: number;
};

/**
 * This returns details about the transaction/purchase that are needed for
 * an invoice and for EDI.
 *
 *
 * @param param0
 * @returns
 */
export const invoiceTransactionMetaData = ({
  transaction,
}: {
  transaction: PatientTransaction;
}): respType => {
  let resp: respType = {
    serviceTotal: 0,
    involvesSpinalManipulation: false,
    patientAmountPaid: 0,
  };

  if (!transaction || !transaction.items || !transaction.items.length) {
    return resp;
  }

  resp = transaction.items.reduce(
    (acc, item) => {
      if (item.subtype === TransactionItemSubtypeEnum.Service) {
        acc.serviceTotal += Number(item.amount) * Number(item.units);

        if (
          item.code === '98940' ||
          item.code === '98941' ||
          item.code === '98942' ||
          item.code === '98940S'
        ) {
          acc.involvesSpinalManipulation = true;
        }
      } else if (item.subtype === TransactionItemSubtypeEnum.PatientPayment) {
        acc.patientAmountPaid += Number(item.amount);
      } else if (item.subtype === TransactionItemSubtypeEnum.PatientCredit) {
        acc.patientAmountPaid += Number(item.amount);
      } else if (isaRefundItem(item)) {
        acc.patientAmountPaid -= Number(item.amount);
      }

      return acc;
    },
    resp // Reduce initial value from default response.
  );

  return resp;
};

type _ediItemFields = {
  qualifier: string;
  code: string;
  name: string;
};

// Partial claim line selection by PTI id, keyed by invoice (single-invoice only).
export type ServicePtiIdSelection = { [invoiceId: number]: number[] };

/**
 * ISL4-3 dual-key line selection: stableId (= PTI.displayData.stableId, survives
 * savePatientTransaction's delete+reinsert) resolved first; ptiId is the
 * fallback for lines whose stableId has never been persisted.
 */
export type ServiceLineKey = { ptiId: number; stableId?: string | null };
export type ServiceLineKeySelection = { [invoiceId: number]: ServiceLineKey[] };

export type CreateEdiPostContextType = {
  billingKey?: string;
  invoiceIds?: number[];
  invoice?: Invoice;
  payorPriorityCode?: string;
  meta?: {
    [key: number]: {
      delayReason?: string;
      generation?: string;
      claimControlNumber?: string;
    };
  }; // Additional details to be sent to the EDI.
  targetStatus?: string;
  debug?: boolean;
  trace?: boolean;
  update?: boolean;
  allOrNone?: boolean;
  batch?: boolean;
  mock?: {
    enable: boolean;
    values: { [key: string]: any };
  };
  testUpload?: boolean;
  /** Force paper claim processing (fetch all fields needed for CMS1500) regardless of invoice.electronicBilling */
  forcePaperClaim?: boolean;
  /** True only when the paper CMS-1500 being built is the real, complete form.
   * The blank "Fields" preview passes false — it must never lock or freeze claim info. */
  paperClaimComplete?: boolean;
  createClaimLineEvents?: boolean; // ISL 'gate' flag
  /** `patientRecordClaimInfo` gate — drives resolver-backed reads and snapshot writes in createBatchSet. */
  usePatientClaimInfo?: boolean;
  /** @deprecated ISL4-3: superseded by `serviceLineKeys`; still honored for queue payloads/clients enqueued pre-deploy. */
  servicePtiIds?: ServicePtiIdSelection;
  serviceLineKeys?: ServiceLineKeySelection; // partial claim line selection, stableId-first (single-invoice only)
};

export type CreateEdiPostType = {
  context: CreateEdiPostContextType;
};

/**
 * statusData.version 2: `items` holds only the latest item and the history lives in
 * ClinicTransactionEvent. Absent: legacy, `items` is the full history (newest first) and
 * is never migrated.
 */
export const INVOICE_STATUS_DATA_VERSION = 2;

export type InvoiceStatusData = {
  version?: number;
  items: InvoiceStatusDataItemType[];
};

/**
 * Create an empty invoice status data object. This returns a new
 * object, so you don't need to worry about mutating the original object.
 *
 * @returns An empty invoice status data object.
 */
export const emptyInvoiceStatusData = (): InvoiceStatusData => {
  return { items: [] } as InvoiceStatusData;
};

/**
 * Invariant tail of the createBatchSet missing-diagnosis issue text. The
 * claims-tab "Missing diagnosis" filter matches statusData issues on this
 * string — change it in lockstep with the writer or the filter goes blind.
 */
export const MissingDiagnosisIssueSuffix = 'must have at least one diagnosis.';

/**
 * Source tracking enum for invoice status data items.
 * Each value is spaced by 20 to allow for future insertions.
 * The value indicates where the status data item was created.
 *
 * NEVER CHANGE THESE OR REUSE A NUMBER!!!!! Just add new ones.
 * We always need to be able to decipher the chain of events and
 * modifying these will break that.
 */
export enum InvoiceStatusDataSource {
  IntegrationInvoiceStatusUpdate = 20,
  CreateBatchSetTest = 40,
  CreateBatchSetSubmit = 60,
  CreateBatchSetPartialSubmit = 70,
  /** @deprecated No writer. Kept so historical statusData items resolve by name. */
  SetInvoiceStatus = 80,
  ParseResponseFile_277 = 100,
  ParseResponseFile_835 = 120,
  ParseResponseFile_999 = 140,
  UpdateInvoiceStatusFromClaimResponse_Processed = 160,
  UpdateInvoiceStatusFromClaimResponse_DeniedRejected = 180,
  UpdateInvoiceStatusFromClaimResponse_Received = 200,
  UpdateInvoiceStatusFromClaimResponse_ForwardedTransferred = 220,
  UpdateInvoiceStatusFromClaimResponse_ForwardedTransferred_NextInvoice = 240,
  UpdateInvoiceStatusFromClaimResponse_Default = 260,
  IntegrationInvoiceAdjust = 280,
  ClaimResponseEra_PostPayment = 300,
  ClaimResponseEra_ClearUnposted = 320,
  ClaimResponseEra_ReapplyAdjudication = 340,
  ClaimResponseEra_Reprocess = 360,
  TransactionClaimAssociation = 380,
  DevtoolsInvoicePanelMove = 400,
  NextInvoiceByPriority = 420,
  PayorPaymentUnapply = 440,
  UnpostInvoice = 460,
  BusinessRules_ResolveInvoiceIssues = 480,
  BusinessRules_PrimaryInvoiceWaiting = 500,
  SetInvoiceValues = 520,
  PatientBillingInvoiceHelpers = 540,
  InitialInvoiceStatusData = 560,
  /** @deprecated No writer. Kept so historical statusData items resolve by name. */
  SignatureChangeArchive = 580,
  EraApply = 600,
  TransactionSave = 620,
  AccumulatePayorLineItems_EnsurePlis = 640,
  BatchQueue837 = 660,
  BatchQueue837Failed = 680,
  NotificationOnlyRejected = 700,
  BillerFinalize = 720,
  BillerFinalizeAutoRevoke = 740,
  BillerUnfinalize = 760,
  EraClaimResponseDrain_Adjudicated = 780,
  EraClaimResponseDrain_Crossover835 = 800,
  EraClaimResponseDrain_Repoint = 810,
  UpdateInvoiceStatusFromClaimResponse_ISL_PerLine = 820,
  BillerDispute = 840,
  BillerUnpostRevert = 860,
  ClaimResponseEra_RepostAdjudicated = 880,
  BillerUndispute = 900,
  BillerUnpostRestoreSibling = 920,
  TakebackAutoDispute = 940,
  ConnectorClaimRejected = 960,
  BillerSetAsFinal = 980,
  /** [13277519972] Biller folded one 835's CLP into another CLP of the same claim. */
  BillerMergeClaimResponse = 1000,
  /** [13277519972] Biller reversed a claim response merge. */
  BillerUnmergeClaimResponse = 1020,
}

/** Queue-specific issue text for non-cause invoices in a failed chunk. */
export const BATCH_QUEUE_COLLATERAL_FAILURE_TEXT =
  'Another invoice in this chunk failed validation.';

export type InvoiceStatusDataItemType = {
  claimControlNumber?: string;
  /** PatientClaimInfo transmission-freeze rev this status item transmitted. */
  claimInfoRev?: number;
  delayReason?: string;
  exchangeId?: string;
  final: string;
  generation?: string;
  interchangeControlNumber?: string;
  issues: BusinessRuleItemType[];
  notes: BusinessRuleItemType[];
  // Archived snapshots: pre-ISL7-1 items carry the legacy `controlNumber` key.
  payorLineItems?: ArchivedPayorLineItemSnapshot[];
  previous: string;
  target: string;
  ts: string;
  userId: string;
  userName?: string;
  electronicBilling?: boolean;
  src: InvoiceStatusDataSource;
};

const initialInvoiceStatusData = ({ userId, invoice }: { userId: string; invoice?: Invoice }) => {
  return {
    version: INVOICE_STATUS_DATA_VERSION,
    items: [
      {
        ts: Date.now().toString(),
        userId,
        previous: '-none-',
        target: InvoiceStatusEnum.Pending,
        final: invoice?.status ?? InvoiceStatusEnum.Pending,
        issues: [],
        notes: [],
        src: InvoiceStatusDataSource.InitialInvoiceStatusData,
      },
    ],
  };
};

export type GetInvoiceNotesResponse = {
  rows: GetInvoiceNotesRow[];
};

export type GetInvoiceNotesRow = {
  id: number;
  note: string;
  createdAt: string;
  userId: string;
  createdBy: string;
};

// This IS NOT an integration invoice.
export type GetInvoiceResponse = Invoice & {
  locationId: number;
  patientTransactionId: number;
  transactionDate: string;
};

export type uiPayorInvoiceLineItem = {
  id: number;
  code: string;
  diags?: string[];
  modifier1?: string;
  modifier2?: string;
  modifier3?: string;
  modifier4?: string;
  units: number;
  amount: number;
  lineItemControlNumber?: string | null; // NULL until the line's first 837 submission
  stableId?: string | null; // persisted displayData.stableId only; NULL when never saved (no read-time minting)
  isDrugCode?: boolean;
  nationalDrugCode?: string;
  ndcUnits?: number;
  ndcUnitType?: string;
};

export const getUndismissedSubmissionIssues = (
  statusData: InvoiceStatusData | string | null | undefined
): BusinessRuleItemType[] => {
  const current =
    typeof statusData === 'string' ? (JSON.parse(statusData) as InvoiceStatusData) : statusData;
  const latest = current?.items?.[0];

  // Read only the current projection. Archived errors may already have been dismissed.
  return (latest?.issues ?? [])
    .filter(
      issue =>
        issue.persistUntilDismissed ||
        latest?.src === InvoiceStatusDataSource.ConnectorClaimRejected
    )
    .map(issue => ({ ...issue, persistUntilDismissed: true }));
};

/**
 * This adds a new item to the invoice status data. It will create an
 * initial empty one if it does not exist. Otherwise, it just addes to
 * the list.
 *
 * Note that the MOST RECENT IS AT THE TOP. That is items[0] is always
 * the latest.
 *
 * @param param0
 * @returns
 */
export const addInvoiceStatusDataItem = ({
  invoice,
  issues = [],
  notes = [],
  payorLineItems,
  revertStatus,
  targetStatus,
  userId,
  userName,
  src,
  exchangeId: inboundExchangeId,
  emitTransactionEvent = true,
}: {
  invoice?: Invoice | null;
  issues?: BusinessRuleItemType[];
  notes?: BusinessRuleItemType[];
  payorLineItems?: PayorLineItemType[];
  revertStatus?: string;
  targetStatus: string;
  userId: string;
  userName?: string;
  src: InvoiceStatusDataSource;
  /**
   * v2: the exchange that carried this event (the 837 for a submission, the 999/277/835 for
   * an acknowledgment); absent for a biller action. Legacy items keep the claim's exchangeId.
   */
  exchangeId?: string | null;
  /** false: the item is still built, but raises no ClinicTransactionEvent; the caller narrates instead. */
  emitTransactionEvent?: boolean;
}) => {
  if (!invoice) throw new Error('Invoice is required.');
  if (!userId) throw new Error('userId is required.');

  let current = null;

  current = invoice.statusData ? invoice.statusData : initialInvoiceStatusData({ userId, invoice });

  if (typeof current === 'string') {
    current = JSON.parse(current) as InvoiceStatusData;
  }

  const isLatestVersion = (current as InvoiceStatusData)?.version === INVOICE_STATUS_DATA_VERSION;
  const persistentIssues = getUndismissedSubmissionIssues(current);
  // Rejection issues match by id, so two with the same text stay two issues.
  const sameIssue = (left: BusinessRuleItemType, right: BusinessRuleItemType): boolean =>
    left.issueId || right.issueId
      ? left.issueId === right.issueId
      : left.text === right.text &&
        JSON.stringify(left.path ?? []) === JSON.stringify(right.path ?? []);
  // A new rejection keeps an earlier rejection's undismissed issues, so each can still be
  // dismissed on its own.
  const isRejection =
    src === InvoiceStatusDataSource.ConnectorClaimRejected ||
    (src === InvoiceStatusDataSource.ParseResponseFile_277 && (issues?.length ?? 0) > 0);
  const earlierRejectionIssues = isRejection
    ? ((current as InvoiceStatusData)?.items?.[0]?.issues ?? []).filter(
        issue => issue.issueId && !persistentIssues.some(existing => sameIssue(existing, issue))
      )
    : [];
  const statusIssues =
    src === InvoiceStatusDataSource.IntegrationInvoiceStatusUpdate && Array.isArray(issues)
      ? issues.filter(
          issue =>
            !issue.persistUntilDismissed &&
            !persistentIssues.some(existing => sameIssue(existing, issue))
        )
      : issues;

  revertStatus = revertStatus || invoice.status;

  if (
    statusIssues &&
    statusIssues.length > 0 &&
    (targetStatus === InvoiceStatusEnum.Received || targetStatus === InvoiceStatusEnum.Reprocessed)
  ) {
    revertStatus = InvoiceStatusEnum.Rejected;
  }

  /**
   * Rule: It makes no sense for a primary to be "waiting" on
   * anything if there are no issues. So, it is at least pending.
   */
  if (invoice.status === InvoiceStatusEnum.Waiting && invoice.priority === 1) {
    revertStatus = InvoiceStatusEnum.Pending;
  }

  /**
   * Helper to sanitize null/undefined values from being saved as strings.
   * Only include the field if it has a real value.
   */
  const sanitizeValue = (value: any): string | undefined => {
    if (value === null || value === undefined) return undefined;
    const stringValue = String(value);

    // Parse as number - if NaN or 0, reject it
    const parsed = parseInt(stringValue, 10);

    if (isNaN(parsed) || parsed === 0) {
      return undefined;
    }

    return stringValue;
  };

  /**
   * String identifiers (uuid exchangeId, alphanumeric payor claim numbers)
   * are not numeric; only reject empty and stringified null/undefined.
   */
  const sanitizeIdentifier = (value: any): string | undefined => {
    if (value === null || value === undefined) return undefined;
    const stringValue = String(value).trim();

    if (!stringValue || stringValue === 'null' || stringValue === 'undefined') {
      return undefined;
    }

    return stringValue;
  };

  const item: Partial<InvoiceStatusDataItemType> = {
    ts: Date.now().toString(),
    userId,
    previous: invoice.status,
    target: targetStatus,
    final:
      statusIssues && Array.isArray(statusIssues) && statusIssues.length === 0
        ? targetStatus
        : revertStatus,
    issues,
    notes,
    src,
  };

  // Keep the status decision based on this event's issues. An earlier rejection
  // remains visible without preventing a later submission or adjudication.
  const visibleIssues = (issues ?? []).map(issue =>
    src === InvoiceStatusDataSource.ConnectorClaimRejected ||
    persistentIssues.some(existing => sameIssue(existing, issue))
      ? { ...issue, persistUntilDismissed: true }
      : { ...issue }
  );

  if (
    src !== InvoiceStatusDataSource.IntegrationInvoiceStatusUpdate &&
    src !== InvoiceStatusDataSource.BusinessRules_ResolveInvoiceIssues
  ) {
    for (const issue of persistentIssues) {
      const duplicateIndex = visibleIssues.findIndex(existing => sameIssue(existing, issue));

      if (duplicateIndex === -1) {
        visibleIssues.push(issue);
      } else {
        visibleIssues[duplicateIndex].persistUntilDismissed = true;
      }
    }
  }
  // Carried after the status decision above, so they never change this event's status.
  for (const issue of earlierRejectionIssues) {
    if (!visibleIssues.some(existing => sameIssue(existing, issue))) {
      visibleIssues.push({ ...issue });
    }
  }
  item.issues = visibleIssues;

  // Only add optional fields if they have real values
  const claimControlNumber = sanitizeIdentifier(invoice.claimControlNumber);
  if (claimControlNumber) item.claimControlNumber = claimControlNumber;

  const exchangeId = sanitizeIdentifier(isLatestVersion ? inboundExchangeId : invoice.exchangeId);
  if (exchangeId) item.exchangeId = exchangeId;

  const interchangeControlNumber = sanitizeValue(invoice.interchangeControlNumber);
  if (interchangeControlNumber) item.interchangeControlNumber = interchangeControlNumber;

  const delayReason = sanitizeValue(invoice.delayReason);
  if (delayReason) item.delayReason = delayReason;

  const generation = sanitizeValue(invoice.generation);
  if (generation) item.generation = generation;

  if (userName) item.userName = userName;

  // Only add payorLineItems if provided, not empty, and not already in a previous status item
  if (payorLineItems && payorLineItems.length > 0) {
    // Create a content-based fingerprint to detect duplicates (not just IDs - PLIs can be updated)
    const sortedPlis = [...payorLineItems].sort((a, b) => Number(a.id) - Number(b.id));
    const newPliFingerprint = ChiroUpJSON.stringify(sortedPlis);

    // Check if this exact PLI content already exists in a previous status item.
    // Version 2 keeps no previous items here (the table does), so every snapshot is kept.
    const existingItems = isLatestVersion ? [] : (current as InvoiceStatusData)?.items || [];
    const isDuplicate = existingItems.some(existingItem => {
      if (!existingItem.payorLineItems || existingItem.payorLineItems.length === 0) {
        return false;
      }

      const sortedExisting = [...existingItem.payorLineItems].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      const existingFingerprint = ChiroUpJSON.stringify(sortedExisting);

      return existingFingerprint === newPliFingerprint;
    });

    if (!isDuplicate) {
      item.payorLineItems = ChiroUpJSON.noNulls(payorLineItems) as PayorLineItemType[];
    }
  }

  const clone = ChiroUpJSON.clone(current, emptyInvoiceStatusData());

  if (isLatestVersion) {
    clone.items = [item as InvoiceStatusDataItemType];
  } else {
    clone.items = clone.items || [];
    clone.items.unshift(item as InvoiceStatusDataItemType);
  }

  if (emitTransactionEvent) {
    emitInvoiceStatusDataItem({
      invoice,
      item: item as InvoiceStatusDataItemType,
      statusData: clone as InvoiceStatusData,
      exchangeId: sanitizeIdentifier(inboundExchangeId) ?? null,
    });
  }

  return clone as InvoiceStatusData;
};

// Helper for invoice timestamp updates - stores UTC epoch milliseconds
// Note: InvoiceTouchedJoin is kept for backward compatibility with existing queries
// that may reference it, even though it's no longer needed for timestamp calculation.
export const InvoiceTouchedJoin = `
  LEFT JOIN PatientTransaction pt ON pt.billingKey = i.billingKey AND pt.clinicId = i.clinicId
  LEFT JOIN ClinicLocations cl ON cl.id = pt.locationId
`;

// [2025-01-10] Fixed: Previously used timezone conversion which produced incorrect
// epoch values. UNIX_TIMESTAMP(NOW()) correctly returns UTC epoch milliseconds.
// Timezone conversion should only happen at DISPLAY time, not STORAGE time.
export const InvoiceTouchedSql = `UNIX_TIMESTAMP(NOW()) * 1000`;

/**
 * [2025-07-02.1532 by Brian] Updated this to a helper function where we could
 * pass some parameters to. For now, we jsut needed some stuff just before the
 * where clause. Keeping it DRY.
 *
 * Hold my nose and type. This is in one place. It needed to be shared between
 * CUP and admin app, and this makes as much sense as anything.
 *
 * Update the status, timestamps, billedAmount and balance. If there
 * are no line items, the amounts do not change. So, if they are null,
 * they stay null. [2024-05-24.1509 by Brian]
 *
 * [2025-01-10] Simplified: tz parameter is now ignored. Timestamps are stored
 * as UTC epoch milliseconds using UNIX_TIMESTAMP(NOW()). No JOIN needed.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getSqlUpdateInvoiceStatus = (beforeWhere?: string, tz?: string | null) => {
  // Normalize: ensure beforeWhere starts with a comma if provided
  let normalizedBeforeWhere = beforeWhere?.trim() || '';

  if (normalizedBeforeWhere && !normalizedBeforeWhere.startsWith(',')) {
    normalizedBeforeWhere = ', ' + normalizedBeforeWhere;
  }

  return `
  UPDATE Invoice i
     SET i.status = :status
       , i.statusData = :statusData
       , i.statusAt = ${InvoiceTouchedSql}
       , i.touched = ${InvoiceTouchedSql}
       , i.issuedDate = IF(:status = '${InvoiceStatusEnum.Submitted}', COALESCE(i.issuedDate, ${InvoiceTouchedSql}), i.issuedDate)
       , i.billedAmount = COALESCE((SELECT SUM(pli.billedAmount) FROM PayorLineItem pli WHERE pli.clinicId = i.clinicId AND pli.billingKey = i.billingKey AND pli.invoiceId = i.id), i.billedAmount)
       , i.balance = COALESCE((SELECT SUM(pli.billedAmount - pli.remitAmount - pli.adjustmentTotal) FROM PayorLineItem pli WHERE pli.clinicId = i.clinicId AND pli.billingKey = i.billingKey AND pli.invoiceId = i.id), i.balance)
    ${normalizedBeforeWhere}
  WHERE i.id = :id
    AND i.clinicId = :clinicId`;
};

// DRY - [2025-01-10] Removed JOIN since InvoiceTouchedSql no longer needs cl.timezone
export const UPDATE_INVOICE_DATA_QUERY = `
  UPDATE Invoice i
     SET i.invoiceData = :invoiceData
       , i.touched = ${InvoiceTouchedSql}
     WHERE i.id = :id
       AND i.clinicId = :clinicId`;

/**
 * Attachment types for PWK01 segment (Report Type Code)
 * Used to identify the type of clinical documentation attached to a claim
 *
 * Re-exported from core/constants/claimAttachmentTypes for consistency
 */
export { CLAIM_ATTACHMENT_TYPES_OPTIONS as AttachmentTypeOptions } from '@chiroup/core/constants/claimAttachmentTypes';

// Legacy enum - kept for backwards compatibility but prefer using string values from CLAIM_ATTACHMENT_TYPES
export enum AttachmentTypeEnum {
  Chiropractic = 'CB',
  Diagnostic = 'DG',
  Support = 'OZ',
}

/**
 * Transmission methods for PWK02 segment (Transmission Code)
 * Defines how the attachment will be transmitted to the payor
 */
export enum TransmissionMethodEnum {
  AvailableOnRequest = 'AA', // Available on Request at Provider Site
  ByMail = 'BM', // By Mail
  Electronically = 'EL', // Electronically Only (default)
  Email = 'EM', // Email
  FileTransfer = 'FT', // File Transfer
  Fax = 'FX', // By Fax
}

export const TransmissionMethodOptions = [
  {
    value: TransmissionMethodEnum.AvailableOnRequest,
    text: 'AA - Available on Request at Provider Site',
  },
  { value: TransmissionMethodEnum.ByMail, text: 'BM - By Mail' },
  { value: TransmissionMethodEnum.Electronically, text: 'EL - Electronically Only' },
  { value: TransmissionMethodEnum.Email, text: 'EM - Email' },
  { value: TransmissionMethodEnum.FileTransfer, text: 'FT - File Transfer' },
  { value: TransmissionMethodEnum.Fax, text: 'FX - By Fax' },
];

/**
 * Invoice Attachment type
 * Supporting clinical documentation attached to a payor invoice for claim submission
 */
export type InvoiceAttachment = {
  id: number;
  invoiceId: number;
  clinicId: number;
  attachmentType: AttachmentTypeEnum;
  transmissionMethod: TransmissionMethodEnum;
  attachmentName: string;
  createdAt: number;
  createdBy?: string;
  createdByName?: string;
  deletedAt?: number;
  deletedBy?: string;
};

export const MAX_INVOICE_ATTACHMENTS = 10;

/**
 * Statuses where attachments can be added/modified
 * Only certain claim statuses allow modifications to attachments
 */
export const AttachmentEditableStatuses = [
  InvoiceStatusEnum.Hold,
  InvoiceStatusEnum.Waiting,
  InvoiceStatusEnum.Pending,
  InvoiceStatusEnum.Unsubmitted,
  InvoiceStatusEnum.Rejected,
];
